import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {useLocalization} from "../../../../../../hooks/localization";
import temperatureIcon from '../../../../../../assets/images/icons/temperature.png';
import cameraIcon from '../../../../../../assets/images/icons/camera.png';
import plugIcon from '../../../../../../assets/images/icons/controls/plug-close-2.png';
import curtainIcon from '../../../../../../assets/images/icons/curtain.png';
import sensorIcon from '../../../../../../assets/images/icons/sensor.png';
import alarmIcon from '../../../../../../assets/images/icons/alarm.png';
import conciergeIcon from '../../../../../../assets/images/icons/concierge.png';
import scenarioIcon from '../../../../../../assets/images/icons/scenario.png';
import intercomIcon from '../../../../../../assets/images/icons/intercom.png';
import energyIcon from '../../../../../../assets/images/icons/menu_energy.png';
import fancoilIcon from '../../../../../../assets/images/icons/fancoil-menu.png';
import allControlsIcon from "../../../../../../assets/images/icons/all-controls.png"
import lampIconFlat from '../../../../../../assets/images/icons/left-menu/lamp-close.png'
import settingsIconFlat from "../../../../../../assets/images/icons/left-menu/setting.png"
import contactorIconFlat from "../../../../../../assets/images/icons/left-menu/contactor-close.png"
import airConditionerIconFlat from '../../../../../../assets/images/icons/left-menu/air-conditioner-close.png'
import areaIconFlat from "../../../../../../assets/images/icons/left-menu/floors-rooms.png"
import mediaIconFlat from '../../../../../../assets/images/icons/left-menu/control.png'
import { useRPC } from '../../../../../../hooks/rpc'; 
import { useQuestion } from '../../../../../../hooks/question';
import { useMessage } from '../../../../../../hooks/message';
import { PasswordContainer } from '../../../../../../components/PasswordContainer';
import { SettingsPageContainer } from '../../../../../../components/Settings/SettingsPageContainer';
import { SettingInputComponent } from '../../../../../../components/Settings/SettingInputComponent';
import { SettingSelectComponent } from '../../../../../../components/Settings/SettingSelectComponent';
import { useKeyboard } from '../../../../../../hooks/keyboard';
import { SelectItem } from '../../../../../../components/Select';
import { MenuEntity } from '../../../../../../store/control/state';


const BACK_URL = '/settings/custom-menu/sorting-menu/left-menu';

type Icon = {
    name: string;
    iconUrl: string;
    canSpecial?: boolean;
}

export const leftMenuIconList = [
    {
        label: 'intercom',
        value: {
            name: 'intercom',
            iconUrl: intercomIcon,
            canSpecial: true,
        },
    },
    {
        label: 'energy',
        value: {
            name: 'energy',
            iconUrl: energyIcon,
            canSpecial: true,
        },
    },
    {
        label: 'scenario',
        value: {
            name: 'scenario',
            iconUrl: scenarioIcon,
        },
    },
    {
        label: 'area',
        value: {
            name: 'area',
            iconUrl: areaIconFlat,
        },
    },
    {
        label: 'all-controls',
        value: {
            name: 'all-controls',
            iconUrl: allControlsIcon,
        },
    },
    {
        label: 'media',
        value: {
            name: 'media',
            iconUrl: mediaIconFlat,
        },
    },
    {
        label: 'alarm',
        value: {
            name: 'alarm',
            iconUrl: alarmIcon,
            canSpecial: true,
        },
    },
    {
        label: 'concierge',
        value: {
            name: 'concierge',
            iconUrl: conciergeIcon,
            canSpecial: true,
        },
    },
    {
        label: 'sensor',
        value: {
            name: 'sensor',
            iconUrl: sensorIcon,
        },
    },
    {
        label: 'curtain',
        value: {
            name: 'curtain',
            iconUrl: curtainIcon,
        },
    },
    {
        label: 'lamp',
        value: {
            name: 'lamp',
            iconUrl: lampIconFlat,
        },
    },
    {
        label: 'plug',
        value: {
            name: 'plug',
            iconUrl: plugIcon,
        },
    },
    {
        label: 'air_conditioning',
        value: {
            name: 'airConditioner',
            iconUrl: airConditionerIconFlat,
        },
    },
    {
        label: 'camera',
        value: {
            name: 'camera',
            iconUrl: cameraIcon,
            canSpecial: true,
        },
    },
    {
        label: 'contactor',
        value: {
            name: 'contactor',
            iconUrl: contactorIconFlat,
        },
    },
    {
        label: 'heating',
        value: {
            name: 'temperature',
            iconUrl: temperatureIcon,
        },
    },
    {
        label: 'fancoil',
        value: {
            name: 'fancoil',
            iconUrl: fancoilIcon,
        },
    },
    {
        label: 'settings_2',
        value: {
            name: 'settings',
            iconUrl: settingsIconFlat,
            canSpecial: true,
        },
    },
];

export const LeftMenuEditScreen = memo(function LeftMenuEditScreen() {
    const {t} = useLocalization();
    const params = useParams<{ id: string }>();
    const [rpc] = useRPC();
    const question = useQuestion();
    const {success, failure} = useMessage();
    const history = useHistory();

    const menuList = useSelector(state => state.control.menuList);
    const [selectedMenuItem, setSelectedMenuItem] = useState<MenuEntity>(null);

    const [setActiveInput, renderKeyboard, currentInput] = useKeyboard();
    const [menuItemName, setMenuItemName] = useState<string>('');
    const [menuIcon, setMenuIcon] = useState<SelectItem<Icon>>(null);
    const order = menuList.length === 0 ? 0 : menuList.sort((a, b) => b.order - a.order)[0].order + 1;

    const onBackPressed = useCallback(() => {
        history.push(BACK_URL);
    }, [history]);

    const onDeletePressed = useCallback(() => {
        question(t('are_you_sure_to_delete_the_menu'), () => {
            return rpc.deleteMenu(selectedMenuItem.id)
                .then(() => {
                    success(t('menu_successfully_deleted'));
                    onBackPressed();
                })
                .catch(failure);
        });
    }, [t, failure, onBackPressed, question, rpc, selectedMenuItem, success]);

    const isFieldsValid = useCallback((): boolean => {
        if (menuItemName == null || menuItemName.length === 0 || menuItemName.length > 30) {
            failure(t('pls_enter_a_valid_name'));

            return false;
        }

        if (menuIcon == null) {
            failure(t('pls_select_a_icon'));

            return false;
        }

        return true;
    }, [t, failure, menuItemName, menuIcon]);

    const onSavePressed = useCallback(() => {
        if (!isFieldsValid()) {
            return;
        }

        const menuEntity: MenuEntity = {
            id: selectedMenuItem != null ? selectedMenuItem.id : null,
            icon: menuIcon.value.name,
            name: menuItemName,
            order: selectedMenuItem != null ? selectedMenuItem.order : order,
            content_list: selectedMenuItem?.content_list || [],
            is_special: selectedMenuItem?.is_special || false,
            is_hidden: selectedMenuItem?.is_hidden || false
        };

        return rpc.updateMenu(menuEntity)
            .then(() => {
                success(t(menuEntity.id == null ? 'menu_successfully_created' : 'menu_successfully_edited'));
                onBackPressed();
            }).catch(failure);
    }, [t, isFieldsValid, menuIcon?.value?.name, selectedMenuItem, menuItemName, order, rpc, failure, success, onBackPressed]);

    const onBlur = useCallback(() => {
        setActiveInput(null);
    }, [setActiveInput]);

    const buttons = useMemo(() =>
        [
            {
                text: t('delete'),
                className: 'mulberry-wood',
                onClick: onDeletePressed,
                hidden: !(selectedMenuItem) || selectedMenuItem.is_special,
            },
            {
                text: t('save'),
                className: 'green',
                onClick: onSavePressed,
            },
            {
                text: t('cancel'),
                className: 'red',
                url: BACK_URL,
            },
        ], [t, onDeletePressed, onSavePressed, selectedMenuItem],
    );

    const translatedLeftMenuIconList = useMemo(() => leftMenuIconList.map(icon => ({...icon, label: t(icon.label)})), [t]);

    useEffect(() => {
        if (params.id == null) {
            return;
        }

        const item = menuList.find((menuItem) => menuItem.id === params.id);

        if (item === null) {
            return;
        }

        const icon = translatedLeftMenuIconList.find((icon) => icon?.value?.name === item?.icon);

        setSelectedMenuItem(item);
        setMenuItemName(item?.name);
        setMenuIcon(icon || null);
    }, [menuList, params.id, translatedLeftMenuIconList]);

    return (
        <PasswordContainer requiredRole='service' isFullscreen={true}>
            <SettingsPageContainer
                className='service-edit'
                buttons={buttons}
                backUrl={BACK_URL}
                currentInput={currentInput}
            >
                <div className='content-divider'>
                    <div className='content-wrapper'>
                        <SettingInputComponent
                            label={t("menu_name")}
                            placeholder={t("write_menu_name")}
                            value={menuItemName}
                            setValue={setMenuItemName}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}
                        />
                        {
                            renderIf(!selectedMenuItem?.is_special)(() => (
                                <SettingSelectComponent
                                    label={t('menu_icon')}
                                    options={translatedLeftMenuIconList}
                                    onChange={setMenuIcon}
                                    value={menuIcon}/>
                            ))
                        }
                    </div>
                </div>
            </SettingsPageContainer>
            {
                renderKeyboard()
            }
        </PasswordContainer>
    );
});
